/**
 * Guest Service - Manages unauthenticated user sessions
 * Provides client-side state management + server-side persistence via bridge
 */

import { BookProject, GuestSession } from '../types';

const BRIDGE_URL = import.meta.env.VITE_BRIDGE_URL || '';
const GUEST_KEY = 'holistic_author_guest';

// ============ LOCAL STORAGE HELPERS ============

export function generateGuestId(): string {
    return 'guest_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

export function getGuestSession(): GuestSession | null {
    try {
        const stored = localStorage.getItem(GUEST_KEY);
        if (!stored) return null;
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

export function createGuestSession(): GuestSession {
    const session: GuestSession = {
        guestId: generateGuestId(),
        projectId: null,
        firstSegmentGenerated: false,
        createdAt: Date.now(),
        regenerationsUsed: 0
    };
    localStorage.setItem(GUEST_KEY, JSON.stringify(session));
    return session;
}

export function updateGuestSession(updates: Partial<GuestSession>): GuestSession | null {
    const session = getGuestSession();
    if (!session) return null;

    const updated = { ...session, ...updates };
    localStorage.setItem(GUEST_KEY, JSON.stringify(updated));
    return updated;
}

export function clearGuestSession(): void {
    localStorage.removeItem(GUEST_KEY);
    localStorage.removeItem('holistic_author_guest_project');
}

// ============ PROJECT STORAGE (LOCAL) ============

export function getGuestProject(): BookProject | null {
    try {
        const stored = localStorage.getItem('holistic_author_guest_project');
        if (!stored) return null;
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

export function saveGuestProject(project: BookProject): void {
    localStorage.setItem('holistic_author_guest_project', JSON.stringify(project));

    // Update session with project ID
    const session = getGuestSession();
    if (session && !session.projectId) {
        updateGuestSession({ projectId: project.id });
    }
}

// ============ PERMISSION CHECKS ============

export type GuestBlockReason = 'regenerate' | 'audit' | 'second_segment' | 'autopilot';

// Number of free sections/segments allowed for guest and free users
export const FREE_SECTION_LIMIT = 3;

export function canGuestGenerate(
    chapterIdx: number,
    sceneIdx: number,
    segmentIdx: number,
    hasExistingContent: boolean
): { allowed: boolean; reason?: GuestBlockReason } {
    // Allow first FREE_SECTION_LIMIT segments (within first chapter, first scene)
    const isWithinFreeLimit = chapterIdx === 0 && sceneIdx === 0 && segmentIdx < FREE_SECTION_LIMIT;

    if (!isWithinFreeLimit) {
        return { allowed: false, reason: 'second_segment' };
    }

    // Block regeneration (if content already exists)
    if (hasExistingContent) {
        return { allowed: false, reason: 'regenerate' };
    }

    return { allowed: true };
}

export function canGuestAudit(): { allowed: boolean; reason?: GuestBlockReason } {
    // Guests cannot use audit/improve feature
    return { allowed: false, reason: 'audit' };
}

export function canGuestUseAutopilot(): { allowed: boolean; reason?: GuestBlockReason } {
    // Guests cannot use autopilot
    return { allowed: false, reason: 'autopilot' };
}

export function markFirstSegmentGenerated(): void {
    updateGuestSession({ firstSegmentGenerated: true });
}

export function incrementGuestRegenerationsUsed(): void {
    const session = getGuestSession();
    if (session) {
        const current = session.regenerationsUsed || 0;
        updateGuestSession({ regenerationsUsed: current + 1 });
    }
}

// ============ SERVER-SIDE PERSISTENCE ============

interface BridgeResponse {
    success: boolean;
    error?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

async function callBridge(action: string, payload: object): Promise<BridgeResponse> {
    if (!BRIDGE_URL) {
        console.warn('Bridge URL not configured, using local storage only');
        return { success: false, error: 'Bridge not configured' };
    }

    try {
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow', // Required for GAS redirects
            // Use text/plain to avoid CORS preflight with GAS
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify({ action, ...payload })
        });
        const text = await response.text();
        try {
            return JSON.parse(text);
        } catch {
            console.error('Failed to parse bridge response:', text);
            return { success: false, error: 'Invalid response from server' };
        }
    } catch (error) {
        console.error('Bridge call failed:', error);
        return { success: false, error: String(error) };
    }
}

/**
 * Save guest state to server for cross-device persistence
 */
export async function saveGuestStateToServer(guestId: string, project: BookProject): Promise<boolean> {
    const result = await callBridge('saveGuestState', {
        guestId,
        projectData: JSON.stringify(project),
        createdAt: Date.now()
    });
    return result.success;
}

/**
 * Retrieve guest state from server
 */
export async function getGuestStateFromServer(guestId: string): Promise<BookProject | null> {
    const result = await callBridge('getGuestState', { guestId });
    if (result.success && result.projectData) {
        try {
            return JSON.parse(result.projectData);
        } catch {
            return null;
        }
    }
    return null;
}

/**
 * Migrate guest project to a registered user account
 */
export async function migrateGuestToUser(
    guestId: string,
    userId: string,
    projectId: string
): Promise<{ success: boolean; error?: string }> {
    const result = await callBridge('migrateGuestToUser', {
        guestId,
        userId,
        projectId
    });

    if (result.success) {
        // Clear local guest data after successful migration
        clearGuestSession();
    }

    return result;
}

/**
 * Check if guest session exists on server (for recovering session on new device)
 */
export async function checkGuestSessionExists(guestId: string): Promise<boolean> {
    const result = await callBridge('checkGuestSession', { guestId });
    return result.success && result.exists === true;
}
